// @flow
import React, { Component, Fragment } from 'react';
import EventHead from 'components/event/EventHead';
import EventContent from 'components/event/EventContent';
import EventTags from 'components/event/EventTags';
import ScrollToTop from 'components/common/ScrollToTop';
import { BaseActions, CommonActions, DisplayActions, EventsActions } from 'store/actionCreators';
import { connect } from 'react-redux';
import type { State } from 'store';
import type { EventData, TocItem } from 'store/modules/events';
import EventToc from 'components/event/EventToc';
import QuestionModal from 'components/common/QuestionModal/QuestionModal';
import { type ContextRouter, type Location, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { convertToPlainText } from 'lib/common';
import EventPlaceholder from 'components/event/EventPlaceholder';
import throttle from 'lodash/throttle';
import EventLeftSticker from 'components/event/EventLeftSticker/EventLeftSticker';
import EventSeriesInfo from 'components/event/EventSeriesInfo/EventSeriesInfo';

type Props = {
  username: ?string,
  urlSlug: ?string,
  event: ?EventData,
  toc: ?(TocItem[]),
  activeHeading: ?string,
  likeInProcess: boolean,
  currentUsername: ?string,
  askRemove: boolean,
  routerHistory: Location[],
  shouldCancel: boolean,
  logged: boolean,
} & ContextRouter;

class EventViewer extends Component<Props> {
  onActivateHeading = throttle((headingId: string) => {
    EventsActions.activateHeading(headingId);
  }, 250);

  initialize = async () => {
    const { username, urlSlug, shouldCancel } = this.props;
    if (!username || !urlSlug) return;
    try {
      if (shouldCancel) return;
      await EventsActions.readEvent({
        username,
        urlSlug,
      });
      if (!this.props.event) return;
      const { id } = this.props.event;
      EventsActions.getSequences(id);
    } catch (e) {
      console.log(e);
      if (e && e.response && e.response.status === 404) {
        this.props.history.replace('/404');
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.urlSlug !== this.props.urlSlug) {
      EventsActions.unloadEvent();
      this.initialize();
    }
  }

  componentWillUnmount() {
    EventsActions.unloadEvent();
  }

  onSetToc = (toc: ?(TocItem[])) => {
    EventsActions.setToc(toc);
  };

  onToggleLike = () => {
    const { event, likeInProcess } = this.props;
    if (likeInProcess) return;
    if (!event) return;
    if (event.liked) {
      EventsActions.unlike(event.id);
    } else {
      EventsActions.like(event.id);
    }
  };

  onToggleAskRemove = () => {
    EventsActions.toggleAskRemove();
  };

  onConfirmRemove = async () => {
    const { event, history, routerHistory } = this.props;
    EventsActions.toggleAskRemove();
    if (!event) return;
    try {
      await CommonActions.removeEvent(event.id);
      DisplayActions.flushList();
    } catch (e) {
      console.log(e);
    }
    if (routerHistory.length === 0) {
      history.push('/');
      return;
    }
    history.goBack();
  };

  componentDidMount() {
    this.initialize();
    const { hash } = this.props.location;
    if (hash !== '') {
      EventsActions.activateHeading(decodeURI(hash.split('#')[1]));
    }
  }

  informCopy = () => {
    BaseActions.showToast({
      message: '링크가 복사되었습니다',
      type: 'success',
    });
  };

  render() {
    const { event, toc, activeHeading, username, currentUsername, askRemove, logged } = this.props;
    const { onSetToc, onActivateHeading } = this;

    if (!event) return <EventPlaceholder />;
    if (!event.user) return <EventPlaceholder />;
    const plainTextBody = convertToPlainText(event.body);

    const url = `${process.env.BASE_URL}/@${event.user.username}/events/${event.url_slug}`;
    const baseTitle = `${event.title} | 캐시바이`;
    return (
      <Fragment>
        <Helmet>
          <title>{baseTitle}</title>
          <meta name="description" content={plainTextBody} />
          <link rel="canonical" href={url} />
          <meta property="og:url" content={url} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={baseTitle} />
          <meta property="og:description" content={plainTextBody} />
          {event.thumbnail && <meta property="og:image" content={event.thumbnail} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={baseTitle} />
          <meta name="twitter:description" content={plainTextBody} />
          {event.thumbnail && <meta name="twitter:image" content={event.thumbnail} />}
        </Helmet>
        <EventLeftSticker
          logged={logged}
          likes={event.likes}
          liked={event.liked}
          onToggleLike={this.onToggleLike}
          url={this.props.match.url}
          title={event.title}
          username={event.user.username}
          informCopy={this.informCopy}
          disabled={!logged}
        />
        <EventToc
          toc={toc}
          activeHeading={activeHeading}
          onActivateHeading={this.onActivateHeading}
        />
        <EventHead
          id={event.id}
          date={event.released_at}
          title={event.title}
          tags={event.tags}
          categories={event.categories}
          user={event.user}
          likes={event.likes}
          liked={event.liked}
          onToggleLike={this.onToggleLike}
          ownEvent={currentUsername === username}
          onAskRemove={this.onToggleAskRemove}
          logged={logged}
          isPrivate={event.is_private}
          url={this.props.match.url}
          informCopy={this.informCopy}
        />
        {event.series && <EventSeriesInfo series={event.series} username={username || ''} />}
        <EventContent
          thumbnail={event.thumbnail}
          body={event.body}
          onSetToc={onSetToc}
          onActivateHeading={onActivateHeading}
          theme={event.meta.code_theme}
        />
        <EventTags tags={event.tags} />
        <QuestionModal
          open={askRemove}
          title="이벤트 삭제"
          description="이 이벤트를 정말로 삭제하시겠습니까?"
          confirmText="삭제"
          onConfirm={this.onConfirmRemove}
          onCancel={this.onToggleAskRemove}
        />
      </Fragment>
    );
  }
}

export default connect(
  ({ events, pender, user, common }: State) => ({
    currentUsername: user.user && user.user.username,
    event: events.event,
    toc: events.toc,
    activeHeading: events.activeHeading,
    likeInProcess: pender.pending['events/LIKE'] || pender.pending['events/UNLIKE'],
    askRemove: events.askRemove,
    routerHistory: common.router.history,
    shouldCancel: common.ssr && !common.router.altered,
    logged: !!user.user,
  }),
  () => ({}),
)(withRouter(EventViewer));



// WEBPACK FOOTER //
// ./src/containers/event/EventViewer.js